import { ASSET_MANIFEST, isAssetUsable } from "@nano/content";
import { BUILD } from "../../env.ts";

/**
 * Full download of the game's files into Cache Storage before play, used on
 * iOS where Safari loads lazily and may drop data. The service worker serves
 * /assets/* cache-first, so everything stored here works offline afterwards.
 * Only files missing from the cache are fetched, so a new version downloads
 * just its new hashed files.
 */
const CACHE = "runtime-v1";
const MARK_KEY = "nano.preloaded";
const CONCURRENCY = 6;

export interface PreloadProgress {
  readonly done: number;
  readonly total: number;
}

/** Build output + art, from the list emitted at build time (dev: art only). */
async function fileList(): Promise<string[]> {
  try {
    const response = await fetch("/asset-list.json", { cache: "no-store" });
    if (response.ok) {
      const data = (await response.json()) as { version: string; files: string[] };
      if (Array.isArray(data.files)) return data.files;
    }
  } catch {
    // Dev server or offline: fall back to the art manifest.
  }
  return ASSET_MANIFEST.filter((entry) => isAssetUsable(entry, BUILD.allowPendingArt)).map((entry) => entry.file);
}

export function preloadDoneForThisVersion(): boolean {
  try {
    return localStorage.getItem(MARK_KEY) === BUILD.version;
  } catch {
    return false;
  }
}

export async function preloadAll(onProgress: (progress: PreloadProgress) => void): Promise<{ failed: number }> {
  if (typeof caches === "undefined") return { failed: 0 };
  const cache = await caches.open(CACHE);
  const files = await fileList();
  const missing: string[] = [];
  for (const file of files) {
    if (!(await caches.match(file))) missing.push(file);
  }
  const total = files.length;
  let done = total - missing.length;
  let failed = 0;
  onProgress({ done, total });
  const queue = [...missing];
  await Promise.all(
    Array.from({ length: CONCURRENCY }, async () => {
      for (let file = queue.shift(); file; file = queue.shift()) {
        try {
          const response = await fetch(file, { credentials: "omit" });
          if (response.ok) await cache.put(file, response);
          else failed += 1;
        } catch {
          failed += 1;
        }
        done += 1;
        onProgress({ done, total });
      }
    }),
  );
  if (failed === 0) {
    try {
      localStorage.setItem(MARK_KEY, BUILD.version);
    } catch {
      // Private mode: we simply check the cache again next time.
    }
  }
  return { failed };
}
